// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import {
  SyntheticEvent,
  MouseEvent,
  useState,
  useEffect,
  RefObject,
  useMemo,
} from 'react';

import { ViewerItem } from 'presentations/api/schema/user';
import { DateTime, formatToEXDATE, getDateTime } from 'infras/datetime';
import {
  getRepeatUnit,
  ScheduleFormData,
} from 'presentations/views/helpers/routine';
import { defaultScheduleItem } from 'infras/models/routine/data';
import { useRoutineScheduleForm } from '../../organisms/RoutineScheduleForm/hooks';
import { FrequencyType } from 'domains/models/event_type_calendar/frequencyType';
import {
  EventTypeCalendarCreate,
  EventTypeCalendarCreateBody,
} from 'presentations/api/schema/event_type_calendar';
import { destroy, patch, post } from 'infras/rest_client';
import { apiPath } from 'presentations/routes/path';
import EventTypeCalendar from 'domains/models/event_type_calendar';
import { convertCalendarToScheduleItem } from 'presentations/views/helpers/event_type_calendar';
import { PopoverPosition } from '../../atoms/Popover';
import { isMediaMax } from 'presentations/views/styles';
import {
  CREATING_EVENT_CLASS_NAME,
  EventDate,
} from 'presentations/reducers/DashboardCalendar';
import {
  defaultCacheExpiration,
  getPersistentItemWithViewer,
  setPersistentItemWithViewer,
} from 'presentations/views/helpers/storage';

export interface EditCalendar {
  calendar: EventTypeCalendar;
  date: Date | null;
  calendarDate: Date | null;
}

export interface RepeatConfirmType {
  repeatConfirm: RepeatConfirm;
  method: 'edit' | 'delete';
}

export enum RepeatConfirm {
  onlyThisDay = 1,
  all = 2,
}

interface Props {
  viewer: ViewerItem;
  eventTypeId: number | null;
  templateId: number | null;
  timeZone: string;
  calendarTimeZone: string;
  editCalendar: EditCalendar | null;
  isLeftPosition?: boolean;
  calendarContainerRef?: RefObject<HTMLElement> | null;
  selectedEventDate: EventDate | null;
  viewMode: 'calendar' | 'list';
  onUpdatedCalendar?: Function;
  onClose: Function;
  onChangeDateByForm?: (eventDate: EventDate) => void;
}

interface PeriodCalendarFrequency {
  frequencyType: FrequencyType | null;
  frequencyInterval: number | null;
}

const minuteStep = 15;
const INTERVAL_TIME = 200;
const INTERVAL_NUM = 30;
const periodCalendarFrequencyKey = 'periodCalendarFrequency';

let timer: number | null = null;

export function useAvailabilityPopover(props: Props) {
  const t = translateInComponent(translations);
  const isMobile = isMediaMax('smartphone');

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [startedAt, setStartedAt] = useState<DateTime | null>(null);
  const [endedAt, setEndedAt] = useState<DateTime | null>(null);
  const [isRepeat, setIsRepeat] = useState<boolean>(true);
  const [schedules, setSchedules] = useState<ScheduleFormData[]>([]);
  const [endDate, setEndDate] = useState<number>(0);
  const [endDateSpecified, setEndDateSpecified] = useState<DateTime | null>(
    null,
  );

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const [localPosition, setLocalPosition] = useState<PopoverPosition | null>(
    null,
  );

  const [
    repeatConfirmType,
    setRepeatConfirmType,
  ] = useState<RepeatConfirmType | null>(null);

  const [isConfirmDelete, setIsConfirmDelete] = useState<boolean>(false);

  const canSubmit =
    startedAt &&
    endedAt &&
    (!isRepeat || (isRepeat && schedules.length > 0)) &&
    (endDate === 0 || (endDate === 1 && endDateSpecified));

  const utcOffset = useMemo(() => {
    return (
      getDateTime().toUTC().offset -
      getDateTime(undefined, props.calendarTimeZone).offset
    );
  }, [props.calendarTimeZone]);

  useEffect(() => {
    if (props.editCalendar) {
      setEditCalendar(props.editCalendar);
    } else {
      setDefaultData();
    }
  }, [props.editCalendar]);

  // 作成時のデフォルト値
  function setDefaultData() {
    let newStartedAt: DateTime;
    let newEndedAt: DateTime;
    if (props.selectedEventDate) {
      newStartedAt = getDateTime(
        props.selectedEventDate.start,
        props.calendarTimeZone,
      );
      newEndedAt = getDateTime(
        props.selectedEventDate.end,
        props.calendarTimeZone,
      );
    } else {
      newStartedAt = getDateTime(undefined, props.calendarTimeZone);
      newStartedAt = newStartedAt
        .plus({ hour: 1 })
        .plus({
          minutes: minuteStep - (newStartedAt.minute % minuteStep),
        })
        .startOf('minute');

      newEndedAt = newStartedAt
        .plus({
          hour: 1,
        })
        .startOf('minute');

      // 開始終了が別日だったら、終了に合わせる
      if (!newStartedAt.hasSame(newEndedAt, 'day')) {
        newStartedAt = newEndedAt.startOf('day');
        newEndedAt = newStartedAt.plus({
          hour: 1,
        });
      }
    }

    const newSchedules = onClickAddSchedule(newStartedAt);
    setStartedAt(newStartedAt);
    setEndedAt(newEndedAt);

    const cachedFrequency: PeriodCalendarFrequency | null = getPersistentItemWithViewer(
      periodCalendarFrequencyKey,
      props.viewer,
    );
    if (cachedFrequency) {
      if (cachedFrequency.frequencyType === null) {
        setIsRepeat(false);
      } else {
        setSchedules(
          newSchedules.map((schedule) => ({
            ...schedule,
            // frequencyIntervalが0で来ると、想定外の繰り返し設定になってしまうため、
            // デフォルト値は0の場合も1をセットできるように || を使用する
            wdayInterval: cachedFrequency.frequencyInterval || 1,
          })),
        );
      }
    }
  }

  // 編集時のデータ復元
  function setEditCalendar(editCalendar: EditCalendar) {
    const calendar = editCalendar.calendar;

    // 画面で選択しているタイムゾーンより遅れている場合は、繰り返しなしのfullcalendarでeventの表示が前日の日付になってしまうのを防ぐ
    const selectedTimeZoneUtcOffset =
      getDateTime().toUTC().offset -
      getDateTime().setZone(props.timeZone).offset;
    const minuteOffset =
      !calendar.frequencyType && selectedTimeZoneUtcOffset < utcOffset
        ? 60 * 24
        : 0;

    const newStartedAt = getDateTime(
      editCalendar.date ?? calendar.startedAt,
      props.calendarTimeZone,
    )
      .set({
        hour: calendar.startHour,
        minute: calendar.startMinute,
      })
      .plus({ minutes: minuteOffset });
    const newEndedAt = getDateTime(
      editCalendar.date ?? calendar.startedAt,
      props.calendarTimeZone,
    )
      .set({
        hour: calendar.endHour,
        minute: calendar.endMinute,
      })
      .plus({ minutes: minuteOffset });
    setStartedAt(newStartedAt);
    setEndedAt(newEndedAt);

    if (calendar.frequencyType !== null) {
      const schedule = convertCalendarToScheduleItem(calendar);
      if (schedule) {
        setIsRepeat(true);

        const newSchedules: ScheduleFormData[] = [schedule];
        setSchedules(newSchedules);
      }

      if (calendar.endedAt !== null) {
        setEndDate(1);
        setEndDateSpecified(getDateTime(calendar.endedAt, props.timeZone));
      } else {
        setEndDate(0);
      }
    } else {
      setIsRepeat(false);
      setEndDate(0);
    }
  }

  useEffect(() => {
    if (isMobile || !props.calendarContainerRef?.current) return;

    onUpdatedForm();
  }, [props.isLeftPosition, startedAt, props.editCalendar]);

  const onUpdatedForm = () => {
    if (isMobile || !props.calendarContainerRef?.current) return;

    // 前回のタイマーがあれば先に止める
    typeof timer === 'number' && clearInterval(timer);

    let intervalNum = 0;
    timer = setInterval(() => {
      intervalNum++;

      const position = getLocalPosition();
      if (position) {
        setLocalPosition(position);
      }

      // 繰り返し上限、もしくはポジション変更されたらタイマーを止める
      // ポジション変更：localPositionがなくてpositionがある、もしくは位置が異なる
      if (
        intervalNum >= INTERVAL_NUM ||
        (position && !localPosition) ||
        (position &&
          localPosition &&
          (position.left !== localPosition.left ||
            position.top !== localPosition.top))
      ) {
        typeof timer === 'number' && clearInterval(timer);
      }
    }, INTERVAL_TIME);
  };

  const getLocalPosition = () => {
    const element = props.calendarContainerRef?.current?.querySelector<
      HTMLElement
    >(`.${CREATING_EVENT_CLASS_NAME}`);

    if (element) {
      const rect = element?.getBoundingClientRect();

      return {
        left: props.isLeftPosition ? rect.left - 8 : rect.left + rect.width + 8,
        top: rect.top,
      };
    }
  };

  const onChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    switch (name) {
      case 'isRepeat':
        const newValue = Number(value) === 1 ? true : false;
        setIsRepeat(newValue);

        const frequency: PeriodCalendarFrequency = {
          frequencyType: newValue ? FrequencyType.Weekly : null,
          frequencyInterval:
            schedules.length > 0 ? schedules[0].wdayInterval : null,
        };
        setPersistentItemWithViewer(
          periodCalendarFrequencyKey,
          frequency,
          defaultCacheExpiration,
          props.viewer,
        );

        if (newValue) {
          let newStartedAt: DateTime;
          if (!startedAt) {
            newStartedAt = getDateTime({}, props.timeZone);
            setStartedAt(newStartedAt);
          } else {
            newStartedAt = getDateTime(startedAt, props.timeZone);
          }
          schedules.length === 0 && onClickAddSchedule(newStartedAt);
        }
        break;
      case 'endDate':
        setEndDate(Number(value));
        break;
      case 'repeatConfirmType':
        setRepeatConfirmType({
          method: repeatConfirmType?.method ?? 'edit',
          repeatConfirm: Number(value),
        });
        break;
      default:
        break;
    }
  };

  const onClickAddSchedule = (date?: DateTime) => {
    const newSchedules = [...schedules];

    if (date) {
      newSchedules.push(
        defaultScheduleItem(date.weekday === 7 ? 0 : date.weekday, date),
      );
    } else if (startedAt) {
      newSchedules.push(
        defaultScheduleItem(
          startedAt.weekday === 7 ? 0 : startedAt.weekday,
          startedAt,
        ),
      );
    }

    setSchedules(newSchedules);
    return newSchedules;
  };

  const onChangeStartedAt = (datetime: DateTime) => {
    if (!datetime.isValid) return;
    let newStartedAt = datetime.startOf('minute');
    let newEnd = endedAt;

    if (startedAt && endedAt) {
      const duration = endedAt.toMillis() - startedAt.toMillis();
      newEnd = newStartedAt
        .plus({
          milliseconds: duration,
        })
        .startOf('minute');

      // 開始終了が別日だったら、終了を開始の23:45にする
      // (日またぎ防止)
      if (!newStartedAt.hasSame(newEnd, 'day')) {
        newEnd = newStartedAt.set({ hour: 24, minute: 0 });
      }
    }

    // 開始終了ともに同じ時間になってしまっていた時
    if (newEnd && newStartedAt.hasSame(newEnd, 'minute')) {
      if (newEnd.hour === 23 && newEnd.minute === 45) {
        newStartedAt = newStartedAt.set({ hour: 23, minute: 30 });
      } else {
        newEnd = newStartedAt.plus({ minute: 15 });
      }
    }

    setStartedAt(newStartedAt);
    setEndedAt(newEnd);

    if (props.selectedEventDate) {
      props.onChangeDateByForm &&
        props.onChangeDateByForm({
          ...props.selectedEventDate,
          start: newStartedAt.toJSDate(),
          end: newEnd?.toJSDate() ?? props.selectedEventDate.end,
        });
    }

    // 作成もしくは繰り返しなしの編集だったら現在の曜日を繰り返しにも当てはめる
    if (
      (props.editCalendar === null ||
        props.editCalendar.calendar.frequencyType === null) &&
      schedules.length === 0
    ) {
      routineScheduleForm.actions.onChangeStartedAt(newStartedAt);
    }
  };

  const onChangeEndedAt = (datetime: DateTime) => {
    if (!datetime.isValid) return;

    let newStartedAt = startedAt;
    let newEndedAt = datetime.startOf('minute');

    if (startedAt && endedAt) {
      const duration = endedAt.toMillis() - startedAt.toMillis();

      if (startedAt.toMillis() >= newEndedAt.toMillis()) {
        newStartedAt = newEndedAt
          .minus({
            milliseconds: duration,
          })
          .startOf('minute');

        // 開始終了が別日(0時は除く)だったら、開始を終了の0:00にする
        // (日またぎ防止)
        if (
          !newStartedAt.hasSame(newEndedAt, 'day') &&
          newEndedAt.hour > 0 &&
          newEndedAt.minute > 0
        ) {
          newStartedAt = newEndedAt.set({
            hour: 0,
            minute: 0,
          });
        }
      }

      // 繰り返しなしの予定で開始終了が別日だったら、終了に合わせる
      if (
        !isRepeat &&
        newStartedAt &&
        !newStartedAt.hasSame(newEndedAt, 'day') &&
        newEndedAt.hour > 0 &&
        newEndedAt.minute > 0
      ) {
        newStartedAt = newEndedAt.set({
          hour: startedAt.hour,
          minute: startedAt.minute,
        });
      }
    }

    // 開始終了ともに同じ時間になってしまっていた時
    if (newStartedAt && newStartedAt.hasSame(newEndedAt, 'minute')) {
      if (newStartedAt.hour === 0 && newStartedAt.minute === 0) {
        newEndedAt = newEndedAt.set({
          hour: 0,
          minute: 15,
        });
      } else {
        newStartedAt = newEndedAt.minus({ minute: 15 });
      }
    }

    setStartedAt(newStartedAt);
    setEndedAt(newEndedAt);

    if (props.selectedEventDate) {
      props.onChangeDateByForm &&
        props.onChangeDateByForm({
          ...props.selectedEventDate,
          start: newStartedAt?.toJSDate() ?? props.selectedEventDate.start,
          end: newEndedAt.toJSDate(),
        });
    }

    // 作成もしくは繰り返しなしの編集だったら現在の曜日を繰り返しにも当てはめる
    if (
      (props.editCalendar === null ||
        props.editCalendar.calendar.frequencyType === null) &&
      schedules.length === 0
    ) {
      routineScheduleForm.actions.onChangeStartedAt(newStartedAt);
    }
  };

  const onSubmitSchedule = (schedule: ScheduleFormData[], index: number) => {
    setSchedules(schedule);

    const frequency: PeriodCalendarFrequency = {
      frequencyType: FrequencyType.Weekly,
      frequencyInterval: schedule.length > 0 ? schedule[0].wdayInterval : null,
    };
    setPersistentItemWithViewer(
      periodCalendarFrequencyKey,
      frequency,
      defaultCacheExpiration,
      props.viewer,
    );
  };

  const onChangeEndDateSpecified = (dateTime: DateTime) => {
    if (!dateTime.isValid) return;
    setEndDateSpecified(dateTime.endOf('day'));
  };

  const onSubmit = async () => {
    setIsLoading(true);
    const item: EventTypeCalendarCreate | null = getEventTypeCalendarCreate();
    if (!item) return;

    if (props.editCalendar) {
      if (
        props.editCalendar.date &&
        hasChangedCalendar(props.editCalendar.calendar, item, props.timeZone)
      ) {
        // 繰り返しの一部日付の編集で、日時が変わっていたらダイアログ出す
        setIsLoading(false);
        setRepeatConfirmType({
          method: 'edit',
          repeatConfirm: RepeatConfirm.onlyThisDay,
        });
        return;
      } else {
        const data: EventTypeCalendarCreate = item;
        const result = await patch(
          apiPath(
            `/v1/event_type_calendars/${props.editCalendar.calendar.id}/update`,
          ),
          { data },
        );
        if (!result.isSuccess) {
          setIsLoading(false);
          return;
        }
      }
    } else {
      let calendars: EventTypeCalendarCreate[];
      if (isRepeat && schedules.length > 1) {
        calendars = [item];
        for (let i = 1; i < schedules.length; i++) {
          const item: EventTypeCalendarCreate | null = getEventTypeCalendarCreate(
            { scheduleIndex: i },
          );
          if (!item) continue;
          calendars.push(item);
        }
      } else {
        calendars = [item];
      }

      const data: EventTypeCalendarCreateBody = {
        calendars,
      };
      const result = await post(apiPath(`/v1/event_type_calendars/create`), {
        data,
      });
      if (!result.isSuccess) {
        setIsLoading(false);
        return;
      }
    }

    props.onUpdatedCalendar &&
      (await props.onUpdatedCalendar(t('Save calendar setting')));
    setIsLoading(false);
  };

  const getEventTypeCalendarCreate = (options?: {
    isAddEditDateToExceptions?: boolean;
    scheduleIndex?: number;
    isEditCalendarAll?: boolean;
    isEditCalendarAllForPartly?: boolean;
    forceNotRepeat?: boolean;
  }) => {
    if (!canSubmit || !startedAt || !endedAt) {
      return null;
    }

    let calendarStartedAt: DateTime = startedAt;
    let calendarEndedAt: DateTime = endedAt;
    let frequencyType: FrequencyType | null = null;
    let frequencyInterval: number | null = null;
    let wday: number | null = null;
    let specifiedEndedAt: string | null = null;
    let exceptions: string[] | null =
      props.editCalendar?.calendar?.exceptions ?? null;

    // 繰り返し全体の編集なら日はeditCalendar、時間は編集時点にする
    if (options?.isEditCalendarAll && props.editCalendar) {
      calendarStartedAt = getDateTime(
        props.editCalendar.calendar.startedAt,
        props.timeZone,
      ).set({
        hour: startedAt.hour,
        minute: startedAt.minute,
      });
      calendarEndedAt = getDateTime(
        props.editCalendar.calendar.startedAt,
        props.timeZone,
      ).set({
        hour: endedAt.hour,
        minute: endedAt.minute,
      });
    }

    // 繰り返しの一部日付ならstartedAtとendedAtをcalendarに戻す
    if (options?.isEditCalendarAllForPartly && props.editCalendar) {
      calendarStartedAt = getDateTime(
        props.editCalendar.calendar.startedAt,
        props.timeZone,
      ).set({
        hour: props.editCalendar.calendar.startHour,
        minute: props.editCalendar.calendar.startMinute,
      });
      calendarEndedAt = getDateTime(
        props.editCalendar.calendar.startedAt,
        props.timeZone,
      ).set({
        hour: props.editCalendar.calendar.endHour,
        minute: props.editCalendar.calendar.endMinute,
      });
    }

    if (
      options?.isAddEditDateToExceptions &&
      props.editCalendar &&
      props.editCalendar.date
    ) {
      const editDate = formatToEXDATE(
        getDateTime(props.editCalendar.date, props.timeZone)
          .set({
            hour: props.editCalendar.calendar.startHour,
            minute: props.editCalendar.calendar.startMinute,
          })
          .startOf('minute'),
      );
      exceptions = Array.isArray(exceptions)
        ? [...exceptions, editDate]
        : [editDate];
    }

    const scheduleIndex = options?.scheduleIndex ?? 0;
    if (
      isRepeat &&
      schedules.length > scheduleIndex &&
      !options?.forceNotRepeat
    ) {
      const schedule = schedules[scheduleIndex];
      const repeatUnit = getRepeatUnit(
        schedule.monthInterval,
        schedule.dayInterval,
        schedule.wdayInterval,
      );
      switch (repeatUnit) {
        case 'weekly':
          frequencyType = FrequencyType.Weekly;
          frequencyInterval = schedule.wdayInterval;
          wday = schedule.wday;
          break;
        case 'monthly':
        case 'daily':
          break;
      }

      if (endDate === 1 && endDateSpecified) {
        specifiedEndedAt = endDateSpecified.toISO();
      }
    } else {
      specifiedEndedAt = getDateTime({
        year: calendarStartedAt.year,
        month: calendarStartedAt.month,
        day: calendarStartedAt.day,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
        zone: props.timeZone,
      })
        .plus({ day: 1 })
        .toISO();
    }

    const item: EventTypeCalendarCreate = {
      startHour: calendarStartedAt.hour,
      startMinute: calendarStartedAt.minute,
      endHour:
        calendarEndedAt.hour === 0 && calendarEndedAt.minute === 0
          ? 24
          : calendarEndedAt.hour,
      endMinute: calendarEndedAt.minute,
      startedAt: getDateTime({
        year: calendarStartedAt.year,
        month: calendarStartedAt.month,
        day: calendarStartedAt.day,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
        zone: props.timeZone,
      }).toISO(),
      endedAt: specifiedEndedAt,
      frequencyType,
      frequencyInterval,
      wday:
        wday !== null
          ? wday
          : calendarStartedAt.weekday === 7
          ? 0
          : calendarStartedAt.weekday,
      schedulableType: props.eventTypeId
        ? 'EventType'
        : 'EventTypeCalendarTemplate',
      schedulableId: props.eventTypeId || props.templateId,
      exceptions,
      parentEventTypeCalendarId: null,
    };
    return item;
  };

  const hasChangedCalendar = (
    a: EventTypeCalendar,
    b: EventTypeCalendarCreate,
    timeZone: string,
  ) => {
    return (
      getDateTime(a.startedAt, timeZone).toISO() !== b.startedAt ||
      a.startHour !== b.startHour ||
      a.startMinute !== b.startMinute ||
      a.endHour !== b.endHour ||
      a.endMinute !== b.endMinute
    );
  };

  const onClickMenu = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setMenuAnchor(menuAnchor ? null : e.currentTarget);
  };
  const onSelectMenu = async (index: number, e: SyntheticEvent) => {
    e.preventDefault();

    setMenuAnchor(null);

    if (!props.editCalendar) return;

    // 繰り返しありの場合は削除方法を選択させる
    if (isRepeat && schedules.length > 0) {
      setRepeatConfirmType({
        method: 'delete',
        repeatConfirm:
          props.viewMode === 'calendar'
            ? RepeatConfirm.onlyThisDay
            : RepeatConfirm.all,
      });
      return;
    } else {
      setIsConfirmDelete(true);
    }
  };

  const deleteEventTypeCalendar = async (editCalendar: EditCalendar) => {
    setIsLoading(true);
    const result = await destroy(
      apiPath(`/v1/event_type_calendars/${editCalendar.calendar.id}/delete`),
    );
    if (!result.isSuccess) {
      setIsLoading(false);
      return;
    }

    props.onUpdatedCalendar &&
      (await props.onUpdatedCalendar(t('Delete calendar setting')));
    setIsLoading(false);
    setIsConfirmDelete(false);
  };

  const onCloseMenu = () => {
    setMenuAnchor(null);
  };

  const onClosePopover = () => {
    if (isLoading) return;

    props.onClose();
  };

  const onSubmitRepeatConfirm = async () => {
    if (!props.editCalendar || repeatConfirmType === null) return;

    switch (repeatConfirmType.method) {
      case 'edit':
        onSubmitRepeatConfirmForEdit(props.editCalendar, repeatConfirmType);
        break;
      case 'delete':
        onSubmitRepeatConfirmForDelete(props.editCalendar, repeatConfirmType);
        break;
      default:
        break;
    }
  };

  const onSubmitRepeatConfirmForEdit = async (
    editCalendar: EditCalendar,
    repeatConfirmType: RepeatConfirmType,
  ) => {
    switch (repeatConfirmType.repeatConfirm) {
      case RepeatConfirm.onlyThisDay: {
        const item: EventTypeCalendarCreate | null = getEventTypeCalendarCreate(
          {
            forceNotRepeat: true,
          },
        );
        if (!item) return;

        setIsLoading(true);

        // 現在の設定を繰り返しなしで保存
        const startedAtISO =
          startedAt?.startOf('day').toISO() ?? item.startedAt;
        const endedAtISO = getDateTime(startedAtISO).plus({ day: 1 }).toISO();
        const onlyThisDayCalendar: EventTypeCalendarCreate = {
          ...item,
          startedAt: startedAtISO,
          frequencyType: null,
          frequencyInterval: null,
          endedAt: endedAtISO,
          parentEventTypeCalendarId: editCalendar.calendar.id,
        };
        const data: EventTypeCalendarCreateBody = {
          calendars: [onlyThisDayCalendar],
        };
        const result = await post(apiPath(`/v1/event_type_calendars/create`), {
          data,
        });
        if (!result.isSuccess) {
          setIsLoading(false);
          return;
        }
        // 元の繰り返しカレンダーから該当日を除外
        const originalRepeatItem: EventTypeCalendarCreate | null = getEventTypeCalendarCreate(
          { isAddEditDateToExceptions: true, isEditCalendarAllForPartly: true },
        );
        const originalRepeatItemResult = await patch(
          apiPath(
            `/v1/event_type_calendars/${editCalendar.calendar.id}/update`,
          ),
          { data: originalRepeatItem },
        );
        if (!originalRepeatItemResult.isSuccess) {
          setIsLoading(false);
          return;
        }
        break;
      }
      case RepeatConfirm.all: {
        const item: EventTypeCalendarCreate | null = getEventTypeCalendarCreate(
          { isEditCalendarAll: true },
        );
        if (!item) return;

        setIsLoading(true);

        const data: EventTypeCalendarCreate = item;
        const result = await patch(
          apiPath(
            `/v1/event_type_calendars/${editCalendar.calendar.id}/update`,
          ),
          { data },
        );
        if (!result.isSuccess) {
          setIsLoading(false);
          return;
        }
        break;
      }
    }

    props.onUpdatedCalendar &&
      (await props.onUpdatedCalendar(t('Save calendar setting')));
    setIsLoading(false);
  };

  const onSubmitRepeatConfirmForDelete = async (
    editCalendar: EditCalendar,
    repeatConfirmType: RepeatConfirmType,
  ) => {
    switch (repeatConfirmType.repeatConfirm) {
      case RepeatConfirm.onlyThisDay: {
        setIsLoading(true);
        const item: EventTypeCalendarCreate | null = getEventTypeCalendarCreate(
          { isAddEditDateToExceptions: true, isEditCalendarAllForPartly: true },
        );
        if (!item) return;
        const data: EventTypeCalendarCreate = item;
        const result = await patch(
          apiPath(
            `/v1/event_type_calendars/${editCalendar.calendar.id}/update`,
          ),
          { data },
        );
        if (!result.isSuccess) {
          setIsLoading(false);
          return;
        }

        props.onUpdatedCalendar &&
          (await props.onUpdatedCalendar(t('Delete calendar setting')));
        setIsLoading(false);
        break;
      }
      case RepeatConfirm.all: {
        deleteEventTypeCalendar(editCalendar);
        break;
      }
    }
  };

  const onCloseRepeatConfirm = () => {
    setRepeatConfirmType(null);
  };

  const onSubmitConfirmDelete = () => {
    if (!props.editCalendar) return;

    deleteEventTypeCalendar(props.editCalendar);
  };

  const onCloseConfirmDelete = () => {
    setIsConfirmDelete(false);
  };

  const routineScheduleForm = useRoutineScheduleForm({
    startedAt: startedAt,
    isAllday: false,
    schedule: schedules,
    index: 0,
    isWeeklySingleSelect: props.editCalendar !== null,
    onSubmit: onSubmitSchedule,
  });

  return {
    state: {
      isMobile,
      isLoading,
      startedAt,
      endedAt,
      isRepeat,
      schedules,
      endDate,
      endDateSpecified,
      canSubmit,
      menuAnchor,
      localPosition,
      repeatConfirmType,
      isConfirmDelete,
    },
    actions: {
      onChange,
      onChangeStartedAt,
      onChangeEndedAt,
      onSubmitSchedule,
      onChangeEndDateSpecified,
      onSubmit,
      onClickMenu,
      onSelectMenu,
      onCloseMenu,
      onClosePopover,
      onSubmitRepeatConfirm,
      onCloseRepeatConfirm,
      onSubmitConfirmDelete,
      onCloseConfirmDelete,
    },
    routineScheduleForm,
  };
}
